<template>
  <transition name="blm-slide">
    <div v-if="visible" class="blm-wrapper">
      <!-- Loading overlay -->
      <div v-if="loading" class="blm-loading-overlay">
        <svg class="blm-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="blm-spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="blm-spinner__arc" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Header -->
      <div class="blm-header">
        <div class="blm-header__left">
          <button class="blm-icon-btn" @click="$emit('close')">
            <svg class="blm-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span class="blm-header__title">{{ $t('myBets') }}</span>
        </div>
      </div>

      <!-- Content -->
      <div class="blm-content">
        <BetStats :stats="betsStats" class="blm-section" />
        <BetFilters
          :filters="filters"
          :showBetCode="showBetCode"
          :showOnlyMyBetsToggle="showOnlyMyBetsToggle"
          class="blm-section"
          @search="handleSearch"
          @reset="handleReset"
        />

        <!-- Bets list container -->
        <div class="blm-list-container">
          <!-- Mobile list -->
          <div class="blm-mobile-list">
            <BetItem
              v-for="(bet, index) in paginatedBets"
              :key="bet.code + '_' + index"
              :bet="bet"
              @click="openDetail(bet)"
            />
          </div>

          <!-- Desktop table -->
          <table class="blm-table">
            <thead>
              <tr class="blm-table__head-row">
                <th class="blm-th">{{ $t('bets.date') }}</th>
                <th class="blm-th">{{ $t('bets.code') }}</th>
                <th class="blm-th">{{ $t('generic.context') }}</th>
                <th class="blm-th">{{ $t('bets.type') }}</th>
                <th class="blm-th">{{ $t('bets.amount') }}</th>
                <th class="blm-th">{{ $t('bets.winning') }}</th>
                <th class="blm-th blm-th--center">{{ $t('bets.result') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="myBetsAll.length === 0">
                <td colspan="7" class="blm-empty-cell">
                  <svg class="blm-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <div class="blm-empty-text">{{ $t('noMyBets') }}</div>
                </td>
              </tr>
              <tr
                v-for="(bet, index) in paginatedBets"
                :key="bet.code + '_' + index"
                class="blm-table__row"
                :class="{ 'blm-table__row--alt': index % 2 === 0 }"
                @click="openDetail(bet)"
              >
                <td class="blm-td">{{ formatDate(bet.created_at) }}</td>
                <td class="blm-td blm-td--code">{{ showBetCode ? bet.betCode : bet.code }}</td>
                <td class="blm-td blm-td--capitalize">{{ bet.bet_context }}</td>
                <td class="blm-td blm-td--capitalize">{{ $t(bet.type) }}</td>
                <td class="blm-td">{{ bet.currencySymbol }} {{ formatAmount(bet.amount_real) }}</td>
                <td class="blm-td">
                  <span v-if="bet.winning_real" class="blm-td--win">
                    {{ bet.currencySymbol }} {{ formatAmount(bet.winning_real) }}
                  </span>
                  <span v-else class="blm-td--primary">{{ getWinning(bet) }}</span>
                </td>
                <td class="blm-td blm-td--center">
                  <BetStatusIcon :status="bet.result" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="myBetsAll.length > 0 && totalPages > 1" class="blm-pagination">
          <div class="blm-pagination__info">
            {{ $t('showing') || 'Showing' }} {{ paginationStart }} - {{ paginationEnd }}
            {{ $t('of') || 'of' }} {{ myBetsAll.length }} {{ $t('myBets').toLowerCase() }}
          </div>
          <div class="blm-pagination__controls">
            <button class="blm-page-btn" :disabled="currentPage === 1" @click="prevPage">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div class="blm-page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="blm-page-num"
                :class="{ 'blm-page-num--active': page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
            </div>
            <button class="blm-page-btn" :disabled="currentPage === totalPages" @click="nextPage">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div v-else-if="myBetsAll.length > 0" class="blm-pagination__single">
          {{ $t('showing') || 'Showing' }} {{ myBetsAll.length }} {{ $t('myBets').toLowerCase() }}
        </div>
      </div>

      <!-- Bet Detail Modal -->
      <BetDetailModal
        :visible="showDetailModal"
        :bet="selectedBet"
        :loading="detailLoading"
        :cashout="cashout"
        :cashout-prematch="cashoutPrematch"
        @close="closeDetailModal"
        @cashout-success="handleCashoutSuccess"
        @cashout-error="handleCashoutError"
      />
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment-timezone';
import BetItem from './BetItem.vue';
import BetFilters from './BetFilters.vue';
import BetStats from './BetStats.vue';
import BetStatusIcon from './BetStatusIcon.vue';
import BetDetailModal from './BetDetailModal.vue';

export default {
  name: 'BetListModal',
  components: { BetItem, BetFilters, BetStats, BetStatusIcon, BetDetailModal },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    return {
      loading: false,
      currentPage: 1,
      itemsPerPage: 30,
      showDetailModal: false,
      selectedBet: null,
      detailLoading: false,
      cashout: 0,
      cashoutPrematch: 0,
      filters: {
        type: '',
        result: '',
        payed: '',
        code: '',
        betCode: '',
        bet_context: '',
        fromTime: oneMonthAgo.toISOString().substr(0, 10),
        toTime: new Date().toISOString().substr(0, 10),
        onlyPlayedByMe: true
      },
      betsStats: {
        total: 0,
        totalPlayed: 0,
        winner: 0,
        void: 0,
        pending: 0,
        net: 0,
        currency: ''
      }
    };
  },
  computed: {
    ...mapGetters({
      getMyBetsAll: 'user/getMyBetsAll',
      getShortCode: 'settings/getShortCode',
      getUserType: 'user/getType',
      getCashier: 'user/getCashier',
      getPlatformSettings: 'settings/getPlatformSettings',
      getRulesManager: 'rules/getRulesManager',
      getRulesSkin: 'rules/getRulesSkin',
      getCashout: 'settings/getCashout',
      getCashoutLive: 'settings/getCashoutLive',
      getDetailMyBets: 'user/getDetailMyBets'
    }),
    myBetsAll() {
      return this.getMyBetsAll || [];
    },
    showBetCode() {
      return this.getShortCode || false;
    },
    showOnlyMyBetsToggle() {
      return (
        (this.getUserType != null && this.getUserType === 4) ||
        (this.getCashier != null && this.getCashier === true)
      );
    },
    paginatedBets() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.myBetsAll.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.myBetsAll.length / this.itemsPerPage);
    },
    paginationStart() {
      return this.myBetsAll.length === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    paginationEnd() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.myBetsAll.length ? this.myBetsAll.length : end;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const maxVisible = 5;
      if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else if (current <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i);
      } else if (current >= total - 2) {
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        for (let i = current - 2; i <= current + 2; i++) pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    visible: {
      async handler(newValue) {
        if (newValue) {
          await this.initialize();
        } else {
          this.currentPage = 1;
          this.showDetailModal = false;
          this.selectedBet = null;
        }
      }
    },
    getDetailMyBets: {
      async handler(val) {
        if (this.showDetailModal && val && this.selectedBet) {
          this.selectedBet = val;
          await this.checkCashout(val);
          await this.checkCashoutPrematch(val);
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      fetchMyBetsAllWithoutCancelled: 'user/fetchMyBetsAllWithoutCancelled',
      fetchCashout: 'odds/fetchCashout',
      fetchLiveScores: 'odds/fetchLiveScores',
      setDetailMyBet: 'user/setDetailMyBet'
    }),
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatAmount(amount) {
      return parseFloat(String(amount)).toFixed(2);
    },
    getWinning(bet) {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return `${bet.currencySymbol} ${this.formatAmount(bet._multiple_detail.winning_total)}`;
        case 'integral':
          return `${bet.currencySymbol} ${this.formatAmount(bet._integral_detail.winning_total_max)}`;
        case 'system':
          return `${bet.currencySymbol} ${this.formatAmount(bet._system_detail.winning_all_total)}`;
        default:
          return `${bet.currencySymbol} 0.00`;
      }
    },
    async initialize() {
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error initializing bet list:', error);
      } finally {
        this.loading = false;
      }
    },
    async searchBets() {
      const { fromTime, toTime } = this.getEpochTimes();
      const criteria = {};
      if (this.filters.type) criteria.type = this.filters.type;
      if (this.filters.result) criteria.result = this.filters.result;
      if (this.filters.payed) criteria.payed = this.filters.payed;
      if (this.filters.code) criteria.code = this.filters.code;
      if (this.filters.betCode) criteria.betCode = this.filters.betCode;
      if (this.filters.bet_context) criteria.bet_context = this.filters.bet_context;
      if (fromTime) criteria.fromTime = fromTime;
      if (toTime) criteria.toTime = toTime;
      if (this.showOnlyMyBetsToggle) criteria.onlyPlayedByMe = this.filters.onlyPlayedByMe;
      await this.fetchMyBetsAllWithoutCancelled(criteria);
    },
    getEpochTimes() {
      const DEFAULT_TIMEZONE = 'Europe/Malta';
      const timezone = (this.getPlatformSettings && this.getPlatformSettings.timezone) || DEFAULT_TIMEZONE;
      const fromTime = this.filters.fromTime && this.filters.fromTime.trim() !== ''
        ? this.filters.fromTime
        : moment.tz(timezone).startOf('day').format('YYYY-MM-DD');
      const toTime = this.filters.toTime && this.filters.toTime.trim() !== ''
        ? this.filters.toTime
        : moment.tz(timezone).endOf('day').format('YYYY-MM-DD');
      return {
        fromTime: moment.tz(fromTime, timezone).startOf('day').valueOf(),
        toTime: moment.tz(toTime, timezone).endOf('day').valueOf()
      };
    },
    calculateValueMyBets(bets) {
      const count_bets = (bets && bets.length) || 0;
      if (count_bets > 0) {
        let total_played = 0, value_win = 0, value_void = 0, value_pending = 0, currency = '';
        bets.forEach(bet => {
          currency = bet.currencySymbol;
          total_played += Number(bet.amount_real);
          if (bet.result === 'win') value_win += Number(bet.winning_real || 0);
          if (bet.result === 'void') value_void += Number(bet.amount_real);
          if (bet.result === 'pending') {
            switch (bet.type) {
              case 'multiple': case 'single':
                value_pending += Number(bet._multiple_detail.winning_total); break;
              case 'integral':
                value_pending += Number(bet._integral_detail.winning_total_max); break;
              case 'system':
                value_pending += Number(bet._system_detail.winning_all_total); break;
            }
          }
        });
        this.betsStats = {
          total: count_bets,
          totalPlayed: total_played,
          winner: value_win,
          void: value_void,
          pending: value_pending,
          net: value_win - (total_played - value_void),
          currency
        };
      } else {
        this.betsStats = { total: 0, totalPlayed: 0, winner: 0, void: 0, pending: 0, net: 0, currency: '' };
      }
    },
    async handleSearch(filters) {
      this.filters = { ...filters };
      this.currentPage = 1;
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error searching bets:', error);
      } finally {
        this.loading = false;
      }
    },
    async handleReset(filters) {
      const now = new Date();
      const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      this.filters = {
        type: '', result: '', payed: '', code: '', betCode: '', bet_context: '',
        fromTime: oneMonthAgo.toISOString().substr(0, 10),
        toTime: new Date().toISOString().substr(0, 10),
        onlyPlayedByMe: true
      };
      this.currentPage = 1;
      this.loading = true;
      try {
        await this.searchBets();
        this.calculateValueMyBets(this.myBetsAll);
      } catch (error) {
        console.error('Error resetting bets:', error);
      } finally {
        this.loading = false;
      }
    },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
    prevPage() { if (this.currentPage > 1) this.currentPage--; },
    goToPage(page) { if (page >= 1 && page <= this.totalPages) this.currentPage = page; },
    async openDetail(bet) {
      this.detailLoading = true;
      this.selectedBet = bet;
      this.showDetailModal = true;
      try {
        await this.checkCashout(bet);
        await this.checkCashoutPrematch(bet);
        if (bet.result === 'pending') {
          await this._attachLiveScores(bet);
        }
        this.setDetailMyBet(bet);
      } catch (error) {
        console.error('Error opening bet detail:', error);
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedBet = null;
      this.cashout = 0;
      this.cashoutPrematch = 0;
    },
    handleCashoutSuccess() { this.closeDetailModal(); this.initialize(); },
    handleCashoutError() {},
    /**
     * Fills in live score and clock for each leg of a pending bet.
     *
     * Replaces a per-leg loop over `odds/fetchEventResultAndTimeDetail`, which hit
     * the legacy `POST /events/getlive/event`. That route no longer exists in
     * rocky-api — there is no `/events` router at all, only `/v3/events` — so every
     * one of those calls 404'd, the catch swallowed it, and the detail fell through
     * to the empty branch: no score, no minute, and N wasted round trips before the
     * modal opened.
     *
     * One batched `POST /v3/live-scores` instead, the same call the classic MyBets
     * has used since its own migration (`_fetchAndAttachLiveScores`). A 6-leg
     * multiple costs one request rather than six sequential ones.
     *
     * The shapes differ: the legacy answered
     * `livescore.Scoreboard.{Time, Results[].{Position, Value}}`, v3 answers
     * `scoreboard.{clock.seconds, results[].{position, value}}` — lowercase, and
     * raw seconds rather than a pre-divided time. `-1` is v3 for "clock not
     * running".
     *
     * Legs with no score are cleared rather than left alone, which is what the old
     * loop did: a leg that stops being live must not keep showing a stale minute.
     */
    async _attachLiveScores(bet) {
      if (!bet || !Array.isArray(bet.odds)) return;

      const eventIds = [...new Set(bet.odds.map(o => o && o.event_source).filter(Boolean))];
      if (eventIds.length === 0) return;

      const scoreMap = {};
      try {
        const res = await this.fetchLiveScores({ eventIds });

        (res && Array.isArray(res.data) ? res.data : []).forEach(item => {
          if (!item || item.eventId == null) return;
          const sb = item.scoreboard;
          if (!sb || !Array.isArray(sb.results)) return;

          const home = sb.results.find(r => String(r.position) === '1');
          const away = sb.results.find(r => String(r.position) === '2');
          if (!home || !away) return;

          const seconds = sb.clock && sb.clock.seconds;
          const running = seconds != null && String(seconds) !== '-1';

          scoreMap[String(item.eventId)] = {
            home: home.value,
            away: away.value,
            timeActual: running ? parseFloat(seconds) / 60 : null,
          };
        });
      } catch (e) {
        // Scores decorate the bet detail: never block opening it.
        console.error('[LiveScores] fetchLiveScores error:', e);
      }

      bet.odds.forEach((odd, index) => {
        const score = scoreMap[String(odd && odd.event_source)];
        this.$set(bet.odds, index, score
          ? {
            ...odd,
            show_time: true,
            time_actual: score.timeActual != null ? score.timeActual : -1,
            result_actual_home: score.home,
            result_actual_away: score.away,
          }
          : {
            ...odd,
            show_time: false,
            time_actual: '',
            result_actual_home: '',
            result_actual_away: '',
          });
      });
    },

    async checkCashout(bet) {
      if (bet.type === 'integral' || bet.type === 'system') { this.cashout = 0; return; }
      if (!this.getCashoutLive) { this.cashout = 0; return; }

      const rules_skin = this.getRulesSkin || [];
      const rules_manager = this.getRulesManager || [];
      let rcp = rules_skin.find(r => r.detail && r.detail.context === 1 && r.type === 'percentage');
      if (!rcp) rcp = rules_manager.find(r => r.detail && r.detail.context === 1 && r.type === 'percentage');
      const percentage_bookmaker = (rcp && rcp.detail && rcp.detail.value) || 0.97;

      const amount_real = Number(bet.amount_real);
      const applicabile = [];
      const moltiplicatore = [];

      for (let i = 0; i < bet.odds.length; i++) {
        const odd = bet.odds[i];
        const odd_value = Number(odd.outcome_odd_value_for_order);
        if (odd_value > 7) { this.cashout = 0; return; }
        try {
          const odds_instant = await this.fetchCashout({
            id: odd.event_source, outcome_id: odd.id_outcome,
            unique_id: odd.unique_id, ext_id: odd.ext_id
          });
          if (odds_instant && odds_instant.length > 0) {
            for (const item of odds_instant) {
              if (item.odds && item.odds.outcome && item.odds.outcome._attributes && item.odds.outcome._attributes.id === odd.id_outcome) {
                if (item.livescore) bet.odds[i].match_livescore = item.livescore;
                bet.odds[i].sport_event_status = item.sport_event_status && item.sport_event_status._attributes && item.sport_event_status._attributes.status;
                const odd_value_actual = (item.status && item.status.odds) || 0;
                const status_odd = item.status && item.status.status;
                const vincita = amount_real * odd_value;
                const probabilita = 1 / odd_value_actual;
                const vincita_nuova = (vincita * probabilita * percentage_bookmaker) / amount_real;
                if (odd.type === 'live' && (status_odd === 1 || status_odd === 3) && bet.result === 'pending' && odd_value_actual <= 7) {
                  applicabile.push('true');
                  moltiplicatore.push(vincita_nuova);
                } else {
                  applicabile.push('false');
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking cashout:', error);
          applicabile.push('false');
        }
      }

      if (!applicabile.includes('false') && moltiplicatore.length > 0) {
        const importo_cashout = moltiplicatore.reduce((a, b) => a * b, 1) * amount_real;
        this.cashout = importo_cashout > 0 ? Math.abs(importo_cashout) : 0;
      } else {
        this.cashout = 0;
      }
    },
    async checkCashoutPrematch(bet) {
      try {
        if (bet.type === 'integral' || bet.type === 'system') { this.cashoutPrematch = 0; return; }
        if (!this.getCashout) { this.cashoutPrematch = 0; return; }

        const rules_skin = this.getRulesSkin || [];
        const rules_manager = this.getRulesManager || [];

        let rcp = rules_skin.find(r => r.detail && r.detail.context === 0 && r.type === 'percentage');
        if (!rcp) rcp = rules_manager.find(r => r.detail && r.detail.context === 0 && r.type === 'percentage');
        const cashoutrisk = (rcp && rcp.detail && rcp.detail.value) || 20;

        let rcme = rules_skin.find(r => r.detail && r.detail.context === 0 && r.type === 'min_event');
        if (!rcme) rcme = rules_manager.find(r => r.detail && r.detail.context === 0 && r.type === 'min_event');
        const min_events_cashout = (rcme && rcme.detail && rcme.detail.value) || 2;

        let rcmo = rules_skin.find(r => r.detail && r.detail.context === 0 && r.type === 'min_odds');
        if (!rcmo) rcmo = rules_manager.find(r => r.detail && r.detail.context === 0 && r.type === 'min_odds');
        const min_odds_cashout = (rcmo && rcmo.detail && rcmo.detail.value) || 1.2;

        let rcmae = rules_skin.find(r => r.detail && r.detail.context === 0 && r.type === 'max_event');
        if (!rcmae) rcmae = rules_manager.find(r => r.detail && r.detail.context === 0 && r.type === 'max_event');
        const max_events_cashout = (rcmae && rcmae.detail && rcmae.detail.value) || 7;

        const nr_events = bet.odds.length;
        const perc_events = 100 / nr_events;
        const value_percent_cashout = 100 - (perc_events - (perc_events * cashoutrisk) / 100);
        const amount_real = Number(bet.amount_real);
        const in_checkout = [];

        if (bet.bet_context === 'prematch' && bet.result === 'pending') {
          const date_now = new Date();
          let evento_in_corso = false;
          const count_events = bet.odds.length;

          if (count_events >= min_events_cashout && count_events <= max_events_cashout) {
            for (let i = 0; i < count_events; i++) {
              const odd = bet.odds[i];
              let odd_value = Number(odd.outcome_odd_value_for_order);
              if (odd_value < min_odds_cashout) { this.cashoutPrematch = 0; return; }
              if (odd.status_event !== 1) { this.cashoutPrematch = 0; return; }
              if (odd.defined && (odd.result || odd.half_won)) {
                if (odd.half_won) odd_value = odd_value / 2;
                in_checkout.push(odd_value);
              } else if (!odd.defined && odd.time < date_now.getTime()) {
                evento_in_corso = true;
              }
            }
          }

          if (in_checkout.length > 0 && !evento_in_corso) {
            let quota = 1;
            for (const val of in_checkout) quota *= val;
            const vincita = amount_real * quota;
            const profitto = vincita - amount_real;
            const valore_cashout = (profitto * value_percent_cashout) / 100;
            const importo_cashout = profitto - Math.abs(valore_cashout);
            const real_cashout = importo_cashout + amount_real;
            this.cashoutPrematch = real_cashout > 0 ? Math.abs(real_cashout) : 0;
          } else {
            this.cashoutPrematch = 0;
          }
        } else {
          this.cashoutPrematch = 0;
        }
      } catch (e) {
        console.error('Error checking cashout prematch:', e);
        this.cashoutPrematch = 0;
      }
    }
  }
};
</script>

<style scoped>
.blm-wrapper {
  position: fixed;
  inset: 0;
  z-index: 50000;
  top: 70px;
  display: flex;
  flex-direction: column;
  background-color: var(--mybets-card-bg);
}

/* --- Loading overlay --- */
.blm-loading-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.blm-spinner {
  width: 40px;
  height: 40px;
  color: var(--mybets-primary);
  animation: blm-spin 1s linear infinite;
}

.blm-spinner__track { opacity: 0.25; }
.blm-spinner__arc { opacity: 0.75; }

@keyframes blm-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* --- Header --- */
.blm-header {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--mybets-border);
  background-color: rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.blm-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.blm-icon-btn {
  padding: 8px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.blm-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.blm-icon {
  width: 20px;
  height: 20px;
  color: var(--mybets-text);
}

.blm-header__title {
  font-size: var(--font-size-7xl);
  font-weight: 700;
  color: var(--mybets-text);
}

/* --- Content --- */
.blm-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.blm-section {
  margin-bottom: 16px;
}

/* --- List container --- */
.blm-list-container {
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid var(--mybets-border);
  overflow: hidden;
}

/* Mobile list — visible only on small screens */
.blm-mobile-list {
  display: block;
}

.blm-table {
  display: none;
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background-color: transparent;
}

@media (min-width: 768px) {
  .blm-mobile-list { display: none; }
  .blm-table { display: table; }
}

.blm-table__head-row {
  background-color: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid var(--mybets-border);
}

.blm-th {
  padding: 12px;
  text-align: left;
  font-size: var(--font-size-2xl);
  text-transform: uppercase;
  font-weight: 600;
  color: var(--mybets-text-muted);
}

.blm-th--center { text-align: center; }

.blm-table__row {
  border-bottom: 1px solid var(--mybets-border);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.blm-table__row:last-child { border-bottom: none; }
.blm-table__row:hover { background-color: rgba(255, 255, 255, 0.05); }
.blm-table__row--alt { background-color: rgba(255, 255, 255, 0.02); }

.blm-td {
  padding: 12px;
  font-size: var(--font-size-4xl);
  color: var(--mybets-text);
}

.blm-td--code { color: var(--mybets-primary); font-family: monospace; }
.blm-td--capitalize { text-transform: capitalize; }
.blm-td--center { text-align: center; }
.blm-td--win { color: #22c55e; }
.blm-td--primary { color: var(--mybets-primary); }

/* --- Empty state --- */
.blm-empty-cell {
  padding: 32px;
  text-align: center;
  color: var(--mybets-text-muted);
}

.blm-empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  opacity: 0.5;
  display: block;
}

.blm-empty-text {
  font-size: var(--font-size-4xl);
}

/* --- Pagination --- */
.blm-pagination {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

@media (min-width: 480px) {
  .blm-pagination { flex-direction: row; }
}

.blm-pagination__info {
  font-size: var(--font-size-4xl);
  color: var(--mybets-text-muted);
}

.blm-pagination__controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.blm-page-btn {
  padding: 6px 12px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--mybets-border);
  color: var(--mybets-text);
  cursor: pointer;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blm-page-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.blm-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.blm-page-numbers {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.blm-page-num {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: var(--font-size-4xl);
  border: 1px solid var(--mybets-border);
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--mybets-text);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.blm-page-num:hover { background-color: rgba(255, 255, 255, 0.1); }

.blm-page-num--active {
  background-color: rgba(233, 39, 66, 0.15);
  border-color: var(--mybets-primary);
  color: var(--mybets-primary);
  font-weight: 600;
}

.blm-pagination__single {
  margin-top: 16px;
  text-align: center;
  font-size: var(--font-size-4xl);
  color: var(--mybets-text-muted);
}

/* --- Slide transition --- */
.blm-slide-enter-active,
.blm-slide-leave-active {
  transition: transform 0.3s ease-out;
}

.blm-slide-enter {
  transform: translateY(100%);
}

.blm-slide-enter-to {
  transform: translateY(0);
}

.blm-slide-leave {
  transform: translateY(0);
}

.blm-slide-leave-to {
  transform: translateY(100%);
}
</style>
